import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { LogOut } from 'lucide-react';

interface ExitConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when user confirms exit */
  onConfirm: () => void;
  /** Called when user cancels exit */
  onCancel: () => void;
  /** Custom title (default: "Exit App?") */
  title?: string;
  /** Custom description */
  description?: string;
}

/**
 * ExitConfirmDialog - Confirmation dialog shown when user attempts to exit the app
 * 
 * Used by useNavigationHistory hook when user presses back button twice at the root.
 * 
 * @example
 * ```tsx
 * const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
 *   activeTab,
 *   setActiveTab,
 *   options: { preventExit: true, showExitDialog: true }
 * });
 * 
 * return (
 *   <>
 *     <YourAppContent />
 *     <ExitConfirmDialog
 *       open={showExitConfirmDialog}
 *       onConfirm={handleConfirmExit}
 *       onCancel={handleCancelExit}
 *     />
 *   </>
 * );
 * ```
 */
export function ExitConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title = "Exit App?",
  description = "Are you sure you want to exit OilWellX?"
}: ExitConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
              <LogOut className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <AlertDialogTitle className="text-lg">{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-row gap-2 sm:gap-2">
          <AlertDialogCancel 
            onClick={onCancel}
            className="flex-1 sm:flex-none"
          >
            No
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="flex-1 sm:flex-none bg-orange-600 hover:bg-orange-700 focus:ring-orange-600"
          >
            Yes, Exit
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ExitConfirmDialog;
